/**
 * @file Album Upload action.
 */

import axios from 'axios';

import getLogger from '../util/logger';
import { photoUploadSuccess } from './photo-upload';
import { isVideo } from '../apps/pro/components/UploadProgressTracking/helpers';

const log = getLogger('AlbumUploadAction');

export const ALBUM_UPLOAD_REQUEST = 'album/UPLOAD_REQUEST';
export const ALBUM_VIDEO_UPLOAD_REQUEST = 'album/VIDEO_UPLOAD_REQUEST';
export const ALBUM_FILE_UPLOAD_REQUEST = 'album/FILE_UPLOAD_REQUEST';
export const ALBUM_UPLOAD_SUCCESS = 'album/UPLOAD_SUCCESS';
export const ALBUM_UPLOAD_FAILURE = 'album/UPLOAD_FAILURE';
export const ALBUM_UPLOAD_PROGRESS = 'album/UPLOAD_PROGRESS';
export const ALBUM_UPLOAD_RESET = 'album/UPLOAD_RESET';

const albumUploadRequest = photos => {
  return { type: ALBUM_UPLOAD_REQUEST, photos };
};

const albumVideoUploadRequest = videos => {
  return { type: ALBUM_VIDEO_UPLOAD_REQUEST, videos };
};

const albumFileUploadRequest = files => {
  return { type: ALBUM_FILE_UPLOAD_REQUEST, files };
};

const albumUploadSuccess = () => {
  return { type: ALBUM_UPLOAD_SUCCESS };
};

const albumUploadFailure = error => {
  return { type: ALBUM_UPLOAD_FAILURE, error };
};

const albumUploadProgress = photoOrVideo => {
  return { type: ALBUM_UPLOAD_PROGRESS, photoOrVideo };
};

export const albumUploadReset = () => {
  return { type: ALBUM_UPLOAD_RESET };
};

const photoUpload = async (
  accountId,
  albumId,
  photo,
  photosUploadMutation,
  albumAddPhotosMutation,
  albumPhotoGroupAddPhotosMutation,
  photoGroupId,
) => {
  const photoUploadsRes = await photosUploadMutation(accountId, [
    { filename: photo.name, content_type: photo.type },
  ]);

  const photoUploadData = photoUploadsRes.data.photosUpload.photo_uploads[0];

  await axios.put(photoUploadData.upload_url, photo, {
    headers: {
      'Content-Type': photo.type,
    },
  });

  await albumAddPhotosMutation(albumId, [photoUploadData.photo_id]);

  if (photoGroupId) {
    await albumPhotoGroupAddPhotosMutation(photoGroupId, [
      photoUploadData.photo_id,
    ]);
  }

  return photoUploadData;
};

const videoUpload = async (
  albumId,
  video,
  videosUploadMutation,
  videoOpts = {},
) => {
  const videoUploadsRes = await videosUploadMutation([
    {
      filename: video.name,
      content_type: video.type || 'application/octet-stream',
      name: video.name,
      // Always skip, we will always run update afterward regardless.
      video_links_skip_create: true,
      video_links: {
        album_ids: [albumId],
      },
      ...videoOpts,
    },
  ]);

  const videoUploadData = videoUploadsRes.data.videosUpload.videos[0];

  await axios.put(videoUploadData.upload_url, video, {
    headers: {
      'Content-Type': video.type || 'application/octet-stream',
    },
  });

  return videoUploadData;
};

export const albumUpload = async (
  accountId,
  albumId,
  photos,
  dispatch,
  photosUploadMutation,
  albumAddPhotosMutation,
  albumPhotoGroupAddPhotosMutation,
  photoGroupId,
) => {
  log.info('albumUpload() :: Uploading photos...', photos);

  dispatch(albumUploadRequest(photos));

  try {
    for (const photo of photos) {
      dispatch(albumUploadProgress(photo));

      const res = await photoUpload(
        accountId,
        albumId,
        photo,
        photosUploadMutation,
        albumAddPhotosMutation,
        albumPhotoGroupAddPhotosMutation,
        photoGroupId,
      );

      dispatch(photoUploadSuccess({ id: res.photo_id, file: photo }, true));
    }

    dispatch(albumUploadSuccess());

    log.info('albumUpload() :: Upload finished');
  } catch (error) {
    log.error('albumUpload() :: Error:', error);

    dispatch(albumUploadFailure(error));
  }
};

export const albumVideoUpload = async (
  albumId,
  videos,
  dispatch,
  videosUploadMutation,
  videoOpts = {},
) => {
  log.info('albumVideoUpload() :: Uploading videos...', videos);

  dispatch(albumVideoUploadRequest(videos));

  try {
    for (const video of videos) {
      dispatch(albumUploadProgress(video));

      await videoUpload(albumId, video, videosUploadMutation, videoOpts);
    }

    dispatch(albumUploadSuccess());

    log.info('albumUpload() :: Upload finished');
  } catch (error) {
    log.error('albumUpload() :: Error:', error);

    dispatch(albumUploadFailure(error));
  }
};

export const albumFileUpload = async (
  accountId,
  albumId,
  files,
  dispatch,
  photosUploadMutation,
  videosUploadMutation,
  albumAddPhotosMutation,
  albumPhotoGroupAddPhotosMutation,
  photoGroupId,
  videoOpts = {},
) => {
  log.info('albumFileUpload() :: Uploading files...', files);

  dispatch(albumFileUploadRequest(files));

  try {
    for (const file of files) {
      dispatch(albumUploadProgress(file));

      if (isVideo(file)) {
        await videoUpload(albumId, file, videosUploadMutation, videoOpts);
      } else {
        const res = await photoUpload(
          accountId,
          albumId,
          file,
          photosUploadMutation,
          albumAddPhotosMutation,
          albumPhotoGroupAddPhotosMutation,
          photoGroupId,
        );

        dispatch(photoUploadSuccess({ id: res.photo_id, file }, true));
      }
    }

    dispatch(albumUploadSuccess());

    log.info('albumUpload() :: Upload finished');
  } catch (error) {
    log.error('albumUpload() :: Error:', error);

    dispatch(albumUploadFailure(error));
  }
};
